import { useRef, useState, type ReactNode } from 'react';
import { ApiError } from '@signalvote/api-client';
import { ErrorCode, ListPurpose, RecipientSource, type Segment, type SegmentRule, type WorkOrderDto } from '@signalvote/shared';
import { useCreateVoterDataOrder, useUpdateWorkOrder } from '@/hooks/queries';
import { segmentKey } from '@/lib/voterSegmentKey';
import { VoterListSentence, useVoterList } from './VoterListSentence';
import { highestOf, type ListEstimate } from './voterEstimate';
import { formatDollars, nounFor } from './voterPurpose';
import { draftOfSegment } from './voterSavedList';

/** Pairing refusals in plain words, by their stable code. */
const PAIRING_REFUSALS: Partial<Record<string, string>> = {
  [ErrorCode.PairedVoterDataOrderNotForPhones]: 'That isn’t a list for texting. Build the list again here.',
  [ErrorCode.PairedVoterDataOrderNotCellOnly]: 'A text can only reach a cell phone. Keep “have a cell phone” in the list.',
  [ErrorCode.PairedVoterDataOrderAlreadyPaired]: 'That list already goes with another text. Choose Add this list again for a fresh one.',
  [ErrorCode.PairedVoterDataOrderNotDraft]: 'That list has already been ordered. Choose Add this list again for a fresh one.',
  [ErrorCode.PairedVoterDataOrderNotFound]: 'We couldn’t find that list. Choose Add this list again.',
  [ErrorCode.TextSendEditRequiresDraft]: 'This text is already on its way, so its list can’t change.',
};

function pairingErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError) || !error.code) return 'We couldn’t price that list. Try again.';
  // The server's own words carry the smallest order, which only it knows for sure.
  if (error.code === ErrorCode.VoterDataBelowMinimum && error.message) return error.message;
  return PAIRING_REFUSALS[error.code] ?? 'We couldn’t price that list. Try again.';
}

/**
 * A new list to buy keeps only voters with a cell phone: a send can text no other number, and it bills
 * every voter on the list. The server refuses a list without this rule.
 */
const CELL_PHONES: Segment = {
  state: '',
  areas: [],
  rules: [{ id: 'cell', fieldId: 'cell', kind: 'Values', values: ['Yes'] }],
  purpose: ListPurpose.Phones,
  onePerHousehold: false,
};

/** Whether the rules still keep only voters with a cell phone, the server's rule for a list a send can buy. */
const keepsOnlyCellPhones = (rules: readonly SegmentRule[]) =>
  rules.some((rule) => rule.fieldId === 'cell' && rule.kind === 'Values' && rule.values.length === 1 && rule.values[0] === 'Yes');

/** What a cell-phone list's count counts, so it never reads as every voter in the area. */
const cellPhoneVoters = (count: number) => `${nounFor(count, 'voter')} with a cell phone`;

/** Two Segments are the same list when they keep the same voters, whatever their rule ids. */
const listKey = (segment: Segment) => JSON.stringify([segmentKey(segment), segment.purpose]);

export interface BuyList {
  /** The voters to text, read as a sentence, with any pairing or removal refusal under it. */
  sentence: ReactNode;
  /** The list as built so far with its live count and price; null before there is one, or while it is counted. */
  estimate: ListEstimate | null;
  sentenceText: string;
  /** What a bought list delivers for each voter. */
  columns: readonly string[];
  pricePerRecordCents: number;
  /** Why the list can't be bought as it stands: under the smallest order. Null when it can. */
  tooSmall: string | null;
  /** The area picker is open, so the list on screen is not chosen yet and is neither priced nor added. */
  choosingArea: boolean;
  /** The list on screen is the one the send is paired with. */
  isPaired: boolean;
  /** The quote of the paired list, or null before one is paired. */
  quote: WorkOrderDto['voterData'];
  /** Why Add this list waits on the person: the district suggestion above the list is still unanswered. */
  unanswered: string | null;
  /** A list is on screen, chosen and answered for, and its live price isn't under the smallest order, so Add this list can quote it. */
  canPair: boolean;
  pairing: boolean;
  /** Add this list: quotes the list as a Voter data Draft and pairs the send with it. */
  pair: () => void;
  /** Takes the paired list out of the order through the send; the server cancels its Draft. The sentence stays. */
  remove: () => void;
  removing: boolean;
}

/**
 * Buying a list for a Text send: the voters to text as a sentence, always cell phones, priced live as
 * the standalone builder prices it; Add this list, which quotes it as a Voter data Draft and pairs the
 * send with that Draft so both are paid together; and Remove, which lets the send go of it. It restores
 * `pairedOrder` once, when it mounts, so the caller waits for that read first.
 */
export function useBuyList(workOrder: WorkOrderDto, pairedOrder: WorkOrderDto | null): BuyList {
  const quote = pairedOrder?.voterData ?? null;
  const v = useVoterList({
    purpose: ListPurpose.Phones,
    initial: (_catalog, newRuleId) => draftOfSegment(quote?.segment ?? CELL_PHONES, newRuleId),
  });
  const { mutateAsync: createOrder } = useCreateVoterDataOrder();
  const { mutateAsync: update } = useUpdateWorkOrder();
  const [pairing, setPairing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const orderKey = useRef<{ list: string; key: string } | null>(null);

  const estimate = v.segment && !v.counts.error ? v.list : null;
  const minimumOrderCents = v.catalog.data?.minimumOrderCents ?? 0;
  const tooSmall =
    estimate && highestOf(estimate.priceCents) < minimumOrderCents ? `The smallest list is ${formatDollars(minimumOrderCents)}.` : null;

  // One idempotency key per list, so a retry of the same list finds the Draft it already placed.
  const pairList = async () => {
    if (!canPair || !v.segment) return;
    const list = listKey(v.segment);
    if (orderKey.current?.list !== list) orderKey.current = { list, key: crypto.randomUUID() };
    setPairing(true);
    setError(null);
    try {
      const order = await createOrder({ segment: v.segment, sentence: v.sentenceText, idempotencyKey: orderKey.current.key });
      await update({
        id: workOrder.id,
        body: {
          messageText: null,
          sendDate: null,
          sendTime: null,
          amount: null,
          recipientSource: RecipientSource.BoughtList,
          pairedVoterDataWorkOrderId: order.id,
        },
      });
    } catch (err) {
      setError(pairingErrorMessage(err));
    } finally {
      setPairing(false);
    }
  };

  // Back to the campaign's cell phones, the source a Draft starts on: the server unpairs and cancels the list.
  const removeList = async () => {
    setRemoving(true);
    setError(null);
    try {
      await update({
        id: workOrder.id,
        body: { messageText: null, sendDate: null, sendTime: null, amount: null, recipientSource: RecipientSource.CampaignCellPhones },
      });
    } catch {
      setError('We couldn’t take the list out. Try again.');
    } finally {
      setRemoving(false);
    }
  };

  const sentence = v.catalog.isError ? (
    <p role="alert" className="text-sm text-destructive">
      We couldn’t load the voter file. Try again in a minute.
    </p>
  ) : (
    <div className="space-y-3">
      <VoterListSentence list={v} countsAs={keepsOnlyCellPhones(v.rules) ? cellPhoneVoters : undefined} />
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );

  const choosingArea = v.open === 'area';
  const canPair = v.segment !== null && tooSmall === null && !choosingArea && v.unanswered === null;
  return {
    sentence,
    estimate,
    sentenceText: v.sentenceText,
    columns: v.purposeDefinition?.columns ?? [],
    pricePerRecordCents: v.catalog.data?.pricePerRecordCents ?? 0,
    tooSmall,
    choosingArea,
    isPaired: quote !== null && v.segment !== null && listKey(v.segment) === listKey(quote.segment),
    quote,
    unanswered: v.unanswered,
    canPair,
    pairing,
    pair: () => void pairList(),
    remove: () => void removeList(),
    removing,
  };
}
